import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  BadgeCheck,
  CircleUserRound,
  ClipboardCheck,
  HeartHandshake,
  LayoutDashboard,
  Leaf,
  Menu,
  QrCode,
  Radio,
  Sprout,
  Trees,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react'
import { outboxCount, registerConnectivitySync, syncOutbox } from '../lib/offline'

const navigation = [
  { to: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { to: '/campaigns', label: 'Campaigns', icon: Trees },
  { to: '/field/activate', label: 'Field activation', icon: QrCode },
  { to: '/field/plots', label: 'Plot monitoring', icon: ClipboardCheck },
  { to: '/verification', label: 'Verification', icon: BadgeCheck },
  { to: '/care', label: 'Care & issues', icon: HeartHandshake },
  { to: '/tags', label: 'Tag inventory', icon: Radio },
]

interface AppShellProps {
  children: ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const [open, setOpen] = useState(false)
  const [online, setOnline] = useState(() => navigator.onLine)
  const [queued, setQueued] = useState(0)
  const location = useLocation()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const navigationRef = useRef<HTMLElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const title = useMemo(() => {
    const item = navigation.find((entry) => location.pathname.startsWith(entry.to))
    return item?.label ?? 'Tree Passport'
  }, [location.pathname])

  useEffect(() => {
    const refresh = () => void outboxCount().then(setQueued)
    const onOnline = () => {
      setOnline(true)
      void syncOutbox().finally(refresh)
    }
    const onOffline = () => setOnline(false)
    const cleanupSync = registerConnectivitySync()
    refresh()
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('tree-passport:outbox-changed', refresh)
    return () => {
      cleanupSync()
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('tree-passport:outbox-changed', refresh)
    }
  }, [])

  useEffect(() => setOpen(false), [location.pathname])

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 961px)')
    const closeOnDesktop = () => { if (desktop.matches) setOpen(false) }
    desktop.addEventListener('change', closeOnDesktop)
    return () => desktop.removeEventListener('change', closeOnDesktop)
  }, [])

  useEffect(() => {
    if (!open) return
    const controls = () => Array.from(navigationRef.current?.querySelectorAll<HTMLElement>('a, button') ?? []).filter((element) => element.getClientRects().length > 0)
    controls()[0]?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const items = controls()
        const target = event.shiftKey ? items[items.length - 1] : items[0]
        const edge = event.shiftKey ? items[0] : items[items.length - 1]
        if (document.activeElement === edge) { event.preventDefault(); target?.focus() }
        return
      }
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      const target = returnFocusRef.current ?? menuButtonRef.current
      window.setTimeout(() => target?.focus(), 0)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  const openNavigation = () => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setOpen(true)
  }

  const closeNavigation = () => {
    setOpen(false)
    const target = returnFocusRef.current ?? menuButtonRef.current
    window.setTimeout(() => target?.focus(), 0)
  }

  return (
    <div className="app-frame">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <aside ref={navigationRef} id="primary-navigation" className={`sidebar ${open ? 'sidebar--open' : ''}`}>
        <div className="brand-row">
          <Link to="/dashboard" className="brand-mark" aria-label="Tree Passport home">
            <span className="brand-icon"><Sprout size={22} strokeWidth={2.2} /></span>
            <span>
              <strong>Tree Passport</strong>
            </span>
          </Link>
          <button className="icon-button sidebar-close" onClick={closeNavigation} aria-label="Close navigation">
            <X size={20} />
          </button>
        </div>

        <nav className="side-nav" aria-label="Primary">
          <span className="nav-caption">WORKSPACE</span>
          {navigation.slice(0, 2).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}>
              <Icon size={19} strokeWidth={1.9} />
              <span>{label}</span>
            </NavLink>
          ))}
          <span className="nav-caption nav-caption--secondary">FIELD WORK</span>
          {navigation.slice(2).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}>
              <Icon size={19} strokeWidth={1.9} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <Link className="public-preview" to="/tags">
            <Leaf size={18} />
            <span><strong>Public passports</strong><small>Open from tag inventory</small></span>
          </Link>
          <div className="operator-card">
            <span className="avatar"><CircleUserRound size={23} /></span>
            <span><strong>Field operations</strong><small>Local workspace</small></span>
          </div>
        </div>
      </aside>

      {open && <button className="sidebar-backdrop" tabIndex={-1} aria-label="Close navigation" onClick={closeNavigation} />}

      <main id="main-content" className="main-column" tabIndex={-1} inert={open}>
        <header className="topbar">
          <div className="topbar-title">
            <button ref={menuButtonRef} className="icon-button menu-button" onClick={openNavigation} aria-label="Open navigation" aria-controls="primary-navigation" aria-expanded={open}>
              <Menu size={21} />
            </button>
            <div>
              <h1>{title}</h1>
            </div>
          </div>
          <button
            className={`connectivity-pill ${online ? 'is-online' : 'is-offline'}`}
            onClick={() => online && queued > 0 && void syncOutbox()}
            title={queued ? 'Tap to sync queued field records' : online ? 'Connected' : 'Records will save offline'}
          >
            {online ? <Wifi size={15} /> : <WifiOff size={15} />}
            <span>{online ? (queued ? `${queued} queued` : 'Synced') : 'Offline'}</span>
          </button>
        </header>
        <div className="page-wrap">{children}</div>
      </main>

      <nav className="mobile-tabbar" aria-label="Mobile primary" inert={open}>
        {navigation.slice(0, 5).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `mobile-tab ${isActive ? 'is-active' : ''}`}>
            <Icon size={20} strokeWidth={isActiveRoute(to, location.pathname) ? 2.2 : 1.8} />
            <span>{label === 'Field activation' ? 'Activate' : label.split(' ')[0]}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function isActiveRoute(to: string, pathname: string) {
  return pathname.startsWith(to)
}
